import React, { useEffect, useState } from "react";
import Select, { CSSObjectWithLabel } from "react-select";
import { fetchCountriesData, getActiveModule } from "@ts/utils";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import {
    ICountryCodePicker,
    ParentModule,
    SelectOptions,
} from "@ts/common-types";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";

const CountryCodePicker: React.FC<ICountryCodePicker> = ({
    fetchData = true,
    style,
    countryRef,
    selectedCountryDetails,
    setSelectedCountryDetails,
}) => {
    interface ICountryCodePickerOptions extends SelectOptions {
        locale: string[];
    }
    const [countryName, setCountryName] = useState<string>("");
    const [options, setOptions] = useState([]);
    const { selected_country_code, selected_country_locale } = useSelector(
        (state: any) => state.userCountryDetails.value
    );
    const [selectedOption, setSelectedOption] =
        useState<ICountryCodePickerOptions>();
    const { activeModule } = getActiveModule();
    const customStyles = {
        control: (base: CSSObjectWithLabel) =>
            ({
                ...base,
                backgroundColor: "transparent",
                border: `1px solid ${
                    activeModule === ParentModule.SETTINGS
                        ? "#EFEFEF"
                        : "#D8D8D8"
                }`,
                boxShadow: "none",
                "&:hover": {
                    border: `1px solid ${
                        activeModule === ParentModule.SETTINGS
                            ? "#EFEFEF"
                            : "#D8D8D8"
                    }`,
                },
            } as CSSObjectWithLabel),
        placeholder: (base: CSSObjectWithLabel) => {
            return {
                ...base,
                color: "black",
            } as CSSObjectWithLabel;
        },
    };

    useEffect(() => {
        setSelectedOption({
            value: selected_country_code,
            locale: selected_country_locale,
            label: countryName
                ? `${countryName} (${selected_country_code})`
                : selected_country_code,
        });
    }, [selected_country_code, countryName, selected_country_locale]);

    const countryCodeChangeHandler = (option: SelectOptions | any) => {
        setSelectedOption(option);
        setSelectedCountryDetails({
            // country_name: option.label.split(" ")[0],
            selected_country_code: option.value,
            selected_country_locale: option.locale,
        });
    };
    useEffect(() => {
        if (fetchData) {
            fetchCountriesData()
                .then((countriesData) => {
                    if (countriesData) {
                        const formattedOptions = countriesData.map(
                            (cd: any) => {
                                if (cd.alpha2_code === selected_country_code) {
                                    setCountryName(cd.name);
                                }
                                return {
                                    value: cd.alpha2_code,
                                    locale: cd.country_locale,
                                    label: `${cd.name} (${cd.alpha2_code})`,
                                };
                            }
                        );
                        setOptions(formattedOptions);
                    }
                })
                .catch((err) => {
                    console.error(err);
                    Sentry.captureException(err);
                });
        }
    }, [fetchData, selected_country_code]);

    const { isModuleEditDisabled } = usePermissionCheck({
        type: "cellRenderer",
    });

    return (
        <>
            <div style={style && { ...style }}>
                <Select
                    classNamePrefix="mySelect__value-container"
                    ref={countryRef}
                    placeholder={selectedOption?.label}
                    openMenuOnClick={true}
                    options={options}
                    value={selectedCountryDetails["selected_country_code"]}
                    styles={customStyles}
                    onChange={countryCodeChangeHandler}
                    maxMenuHeight={430}
                    openMenuOnFocus={true}
                    components={
                        activeModule === ParentModule.SETTINGS && {
                            DropdownIndicator: () => null,
                            IndicatorSeparator: () => null,
                        }
                    }
                    isDisabled={isModuleEditDisabled}
                />
            </div>
        </>
    );
};

export default CountryCodePicker;
